import * as React from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

export interface SearchableSelectItem {
  id: string | number;
  code?: string;
  title: string;
  description?: string;
}

interface SearchableSelectProps {
  items: SearchableSelectItem[];
  value?: string | number;
  onValueChange: (value: string | number) => void;
  placeholder?: string;
  disabled?: boolean;
  emptyText?: string;
  fallbackDisplayValue?: string;
  allowCreate?: boolean;
  onCreateOption?: (value: string) => void;
  createOptionLabel?: (value: string) => string;
}

export function SearchableSelect({
  items,
  value,
  onValueChange,
  placeholder = "Select item...",
  disabled = false,
  emptyText = "No item found.",
  fallbackDisplayValue,
  allowCreate = false,
  onCreateOption,
  createOptionLabel = (value) => `Use "${value}"`,
}: SearchableSelectProps) {
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState("");

  const selectedItem = items.find((item) => item.id.toString() === value?.toString());

  const filteredItems = React.useMemo(() => {
    if (!search) return items;
    
    const searchLower = search.toLowerCase();
    return items.filter((item) => {
      const codeMatch = item.code?.toLowerCase().includes(searchLower);
      const titleMatch = item.title.toLowerCase().includes(searchLower);
      const descMatch = item.description?.toLowerCase().includes(searchLower);
      
      return codeMatch || titleMatch || descMatch;
    });
  }, [items, search]);

  const normalizedSearch = search.trim();
  const hasExactMatch = React.useMemo(() => {
    if (!normalizedSearch) return false;
    const needle = normalizedSearch.toLowerCase();
    return items.some((item) => {
      return (
        item.title.toLowerCase() === needle ||
        item.code?.toLowerCase() === needle
      );
    });
  }, [items, normalizedSearch]);

  const showCreateOption = allowCreate && normalizedSearch.length > 0 && !hasExactMatch;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full min-w-0 justify-between gap-2 aria-expanded:border-ring aria-expanded:bg-accent"
          disabled={disabled}
        >
          {selectedItem ? (
            <span className="flex min-w-0 flex-1 items-center gap-2">
              {selectedItem.code && (
                <span className="shrink-0 text-muted-foreground">{selectedItem.code}</span>
              )}
              <span className="truncate">{selectedItem.title}</span>
            </span>
          ) : fallbackDisplayValue ? (
            <span className="min-w-0 flex-1 truncate">{fallbackDisplayValue}</span>
          ) : (
            <span className="min-w-0 flex-1 truncate text-left text-muted-foreground">{placeholder}</span>
          )}
          <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[400px] p-0 z-50 bg-background border shadow-md" align="start">
        <Command shouldFilter={false} loop>
          <CommandInput 
            placeholder="Search..." 
            value={search}
            onValueChange={setSearch}
            autoFocus
          />
          <CommandList>
            <CommandEmpty>{emptyText}</CommandEmpty>
            <CommandGroup>
              {filteredItems.map((item) => (
                <CommandItem
                  key={item.id}
                  value={item.id.toString()}
                  onSelect={() => {
                    onValueChange(item.id);
                    setOpen(false);
                    setSearch("");
                  }}
                >
                  <div className="flex flex-col items-start gap-1 w-full">
                    <div className="flex items-center gap-2 w-full">
                      <Check
                        className={cn(
                          "h-4 w-4 shrink-0",
                          value?.toString() === item.id.toString() ? "opacity-100" : "opacity-0"
                        )}
                      />
                      {item.code && <span className="text-muted-foreground text-sm">{item.code}</span>}
                      <span className="font-medium">{item.title}</span>
                    </div>
                    {item.description && (
                      <div className="ml-6 text-sm text-muted-foreground">
                        {item.description}
                      </div>
                    )}
                  </div>
                </CommandItem>
              ))}
              {showCreateOption && (
                <CommandItem
                  value={`create:${normalizedSearch}`}
                  onSelect={() => {
                    if (onCreateOption) {
                      onCreateOption(normalizedSearch);
                    } else {
                      onValueChange(normalizedSearch);
                    }
                    setOpen(false);
                    setSearch("");
                  }}
                >
                  <span className="font-medium">{createOptionLabel(normalizedSearch)}</span>
                </CommandItem>
              )}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
